import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtRoDate } from "../../lib/dateFmt";

/** DrivingHistoryPane — istoric zilnic al mașinilor conduse de angajat.
 * Extracted from PersonDetail.jsx (was lines 3227-3322).
 */
export default function DrivingHistoryPane({ personId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState(50);
  useEffect(() => {
    api.get(`/persons/${personId}/daily-driving-history`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, [personId]);
  if (rows === null) return <div className="card-flat p-6 text-zinc-400">Se încarcă…</div>;
  if (rows.length === 0) {
    return (
      <div className="card-flat p-6 text-center text-zinc-500 text-sm italic">
        Nicio mașină condusă încă. Apare aici după ce persoana e alocată ca șofer pe o mașină în Planificator.
      </div>
    );
  }
  const byCar = new Map();
  for (const r of rows) {
    const cur = byCar.get(r.car_id) || { car_id: r.car_id, label: r.car_label, days: 0, projects: new Set() };
    cur.days += 1;
    if (r.label) cur.projects.add(r.label);
    byCar.set(r.car_id, cur);
  }
  const summary = [...byCar.values()].sort((a, b) => b.days - a.days);
  const visible = rows.slice(0, limit);
  return (
    <div className="space-y-3">
      <div className="card-flat p-4">
        <h3 className="font-display text-base font-bold mb-2">🚗 Sumar mașini conduse</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {summary.map((s) => (
            <button
              key={s.car_id}
              type="button"
              onClick={() => navigate(`/autoturisme/${s.car_id}`)}
              className="text-left p-2 rounded border border-zinc-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
              data-testid={`driving-summary-${s.car_id}`}
            >
              <div className="font-semibold text-sm">{s.label}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{s.days} {s.days === 1 ? "zi" : "zile"} · {s.projects.size} {s.projects.size === 1 ? "proiect" : "proiecte"}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="card-flat p-4">
        <div className="mb-2">
          <h3 className="font-display text-base font-bold">📅 Istoric zilnic</h3>
          <div className="text-xs text-zinc-500 mt-0.5">Cea mai recentă zi prima.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] uppercase tracking-wider text-zinc-600">
                <th className="text-left px-2 py-1.5">Dată</th>
                <th className="text-left px-2 py-1.5">Mașină</th>
                <th className="text-left px-2 py-1.5">Șantier / Regie</th>
                <th className="text-left px-2 py-1.5">Firmă</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={`${r.date}-${r.car_id}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{fmtRoDate(r.date)}</td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => navigate(`/autoturisme/${r.car_id}`)}
                      className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline"
                    >
                      🚗 {r.car_label}
                    </button>
                    {r.driver_explicit && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-700 font-bold" title="Șofer desemnat explicit">★</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: r.color || "#3B82F6" }} />
                      <span className="truncate">{r.label || r.project_name || r.regie_name || "—"}</span>
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-xs text-zinc-600">{r.firma || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > limit && (
          <button type="button" onClick={() => setLimit((l) => l + 50)} className="mt-3 text-xs text-blue-600 hover:underline">
            Arată încă 50 (mai sunt {rows.length - limit})
          </button>
        )}
      </div>
    </div>
  );
}
